import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../../services/api';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import SupportMirrorButton from '../../components/support/SupportMirrorButton';
import Modal from '../../components/common/Modal';
import PlacementHistory from '../../components/admin/PlacementHistory';
import {
  FiPlus,
  FiSearch,
  FiCopy,
  FiMail,
  FiClock,
  FiEdit
} from 'react-icons/fi';
import CustomerForm from '../../components/admin/CustomerForm';
import { getPlacements } from '../../utils/adminV2';
import { useEmailBatch, describeDelivery, deliveryCounts } from '../../hooks/useEmailBatch';

// Colours for the per-customer delivery status in the invitation dialog.
const DELIVERY_COLORS = { ok: '#1e8449', warn: '#b9770e', bad: 'var(--act-danger)' };

// Styled components
const PageContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const FiltersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
`;

const FilterLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin-right: ${({ theme }) => theme.spacing[2]};
`;

const FilterSelect = styled.select`
  padding: ${({ theme }) => theme.spacing[2]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  background-color: white;
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;
  margin-left: auto;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[8]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: ${({ theme }) => theme.spacing[2]};
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.textSecondary};
`;

// Table styles
const TableContainer = styled.div`
  overflow-x: auto;
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background-color: ${({ theme }) => theme.colors.backgroundLight};
`;

const TableHeadCell = styled.th`
  padding: ${({ theme }) => theme.spacing[3]};
  text-align: left;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  white-space: nowrap;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  &:hover {
    background-color: ${({ theme }) => theme.colors.backgroundLight};
  }
`;

const TableCell = styled.td`
  padding: ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  
  ${({ status, theme }) => {
    if (status === true) {
      return `
        background-color: ${theme.colors.success}15;
        color: ${theme.colors.success};
      `;
    } else if (status === false) {
      return `
        background-color: ${theme.colors.error}15;
        color: ${theme.colors.error};
      `;
    } else if (status === 'in_progress') {
      return `
        background-color: ${theme.colors.info}15;
        color: ${theme.colors.info};
      `;
    } else if (status === 'completed') {
      return `
        background-color: ${theme.colors.success}15;
        color: ${theme.colors.success};
      `;
    }
    return '';
  }}
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing[4]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const PaginationInfo = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const AlertMessage = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  
  ${({ type, theme }) => {
    if (type === 'success') {
      return `
        background-color: ${theme.colors.success}15;
        color: ${theme.colors.success};
      `;
    } else if (type === 'error') {
      return `
        background-color: ${theme.colors.error}15;
        color: ${theme.colors.error};
      `;
    }
    return '';
  }}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[6]};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const HistoryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: 3px 8px;
  margin-left: ${({ theme }) => theme.spacing[2]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  cursor: pointer;

  &:hover { background: ${({ theme }) => theme.colors.primaryLight}; }
`;

const normalizedEmail = (value) => String(value || '').trim().toLowerCase();

const normalizedEmployeeIds = (values) => [...new Set((values || [])
  .map((value) => Number(value && typeof value === 'object' ? value.id : value))
  .filter((value) => Number.isInteger(value) && value > 0))]
  .sort((a, b) => a - b);

const sameIds = (left, right) => (
  left.length === right.length && left.every((value, index) => value === right[index])
);

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  // Item 12: per-customer invoicing readiness from VikarCRM. Fetched ONCE for
  // the whole page - the backend makes a single upstream call for every
  // customer - and never allowed to fail the list: an unavailable CRM simply
  // shows no badge rather than an error where a customer list should be.
  const [invoicingStatus, setInvoicingStatus] = useState({ available: false, byCustomer: {} });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    industry: '',
    isActive: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0
  });
  const [alert, setAlert] = useState(null);
  const [industries, setIndustries] = useState([]);

  // Customer editing lives here because both admin and support share this
  // onboarding surface. The general Admin → Users security screen remains
  // admin-only and must not be required for ordinary customer master data.
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [createdInfo, setCreatedInfo] = useState(null);
  const [historyCustomer, setHistoryCustomer] = useState(null);

  // Invitation selection (customers are invited by their user id; the e-mail is
  // Danish and links the Danish customer guide — see backend invitationEmail.js).
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [inviting, setInviting] = useState(false);
  const [inviteReport, setInviteReport] = useState(null);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const navigate = useNavigate();
  // Invitations queue behind the mail limit (v1.42.0): follow the batch so the
  // dialog shows "Sent 09:41" / "Failed: …" per customer, not a hopeful "E-mailed".
  // Only when something was queued: a batch with no rows does not exist.
  const inviteBatchId = (inviteReport && inviteReport.queued > 0 && inviteReport.batchId) || null;
  const { batch: inviteBatch } = useEmailBatch(inviteBatchId, { enabled: Boolean(inviteBatchId) });
  const inviteCounts = deliveryCounts(inviteReport && inviteReport.results, inviteBatch);

  // Fetch reference data (industries) once on mount
  useEffect(() => {
    adminService.getIndustryOptions()
      .then(data => setIndustries(data || []))
      .catch(error => console.error('Error fetching industries:', error));
  }, []);

  // Fetch the customer list whenever filters or page change
  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.page]);

  // Loaded alongside the customers. Deliberately swallows every failure: this
  // is a decoration on a page whose job is listing customers, and a CRM outage
  // must not take that page down.
  const fetchInvoicingStatus = async () => {
    try {
      const res = await adminService.getInvoicingCustomerStatus();
      const byCustomer = {};
      (res.customers || []).forEach((row) => { byCustomer[row.customerId] = row; });
      setInvoicingStatus({ available: Boolean(res.available), byCustomer });
    } catch (err) {
      setInvoicingStatus({ available: false, byCustomer: {} });
    }
  };

  const renderInvoicingStatus = (customerId) => {
    if (!invoicingStatus.available) return <span title="Configure the CRM invoicing connection and retry"><Badge status={false}>Check CRM setup</Badge></span>;
    const row = invoicingStatus.byCustomer[customerId];
    if (!row) return <span>—</span>;
    if (row.state === 'ready') return <span title="Company settings are ready. Review dated work and pricing in Invoicing before creating a draft."><Badge status>Setup ready</Badge></span>;
    if (row.state === 'unknown') return <span title="VikarCRM has no record of this customer">Unknown</span>;
    const missing = (row.missing || []).join(', ');
    return (
      <span title={missing ? `Missing in VikarCRM: ${missing}` : 'Invoicing is not set up in VikarCRM'}>
        <Badge status={false}>Incomplete</Badge>
        {row.crmUrl && (
          <a href={row.crmUrl} target="_blank" rel="noopener noreferrer" style={{ marginLeft: '0.35rem', fontSize: '0.75rem' }}>Set up</a>
        )}
      </span>
    );
  };

  const fetchCustomers = async () => {
    try {
      setLoading(true);

      // Only send non-empty filters; convert 'true'/'false' to boolean for isActive
      const params = {
        page: pagination.page,
        ...(filters.industry && { industry: filters.industry }),
        ...(filters.search && { search: filters.search }),
        ...(filters.isActive !== '' && { isActive: filters.isActive === 'true' })
      };

      const response = await adminService.getCustomers(params);
      
      setCustomers(response.customers);
      setPagination({
        page: response.page,
        totalPages: response.totalPages,
        total: response.total
      });
    } catch (error) {
      console.error('Error fetching customers:', error);
      setAlert({
        type: 'error',
        message: 'Failed to load customers. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    // Reset to first page when changing filters
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearchChange = (e) => {
    const { value } = e.target;
    // Debounce search input
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: value }));
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 300);
  };

  const handleNextPage = () => {
    if (pagination.page < pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: prev.page + 1 }));
    }
  };

  const handlePrevPage = () => {
    if (pagination.page > 1) {
      setPagination(prev => ({ ...prev, page: prev.page - 1 }));
    }
  };

  // Creation errors are thrown so CustomerForm can display them inline in the
  // dialog. An assignment failure AFTER the customer exists (e.g. the 409
  // EMPLOYEE_ALREADY_ASSIGNED one-customer rule) must not keep the dialog open —
  // re-submitting would create a duplicate — so it becomes a page alert instead.
  const handleCreateCustomer = async (customerData) => {
    const { assignedEmployees, ...payload } = customerData;
    const createdCustomer = await adminService.createCustomer(payload);

    // Sync employee assignments (user ids) for the new customer
    let assignmentError = null;
    if (createdCustomer && createdCustomer.id && assignedEmployees && assignedEmployees.length > 0) {
      try {
        await adminService.assignEmployeesToCustomer(createdCustomer.id, assignedEmployees);
      } catch (error) {
        console.error('Error assigning employees:', error);
        assignmentError = error;
      }
    }

    setShowAddModal(false);
    if (assignmentError) {
      setAlert({
        type: 'error',
        message: `The customer was created, but assigning employees failed: ${assignmentError.message || 'Please assign them from the Employees page.'}`
      });
    } else {
      setAlert({
        type: 'success',
        message: 'Customer created successfully.'
      });
    }
    setCreatedInfo({ name: (createdCustomer && createdCustomer.name) || payload.name });
    fetchCustomers();
  };

  const handleUpdateCustomer = async (customerData) => {
    if (!editingCustomer) return;

    const { assignedEmployees: requestedAssignments = [], ...payload } = customerData;
    const assignedEmployees = normalizedEmployeeIds(requestedAssignments);
    const previousAssignments = normalizedEmployeeIds(editingCustomer.assignedEmployees);
    const assignmentsChanged = !sameIds(assignedEmployees, previousAssignments);
    const emailChanged = normalizedEmail(payload.contactEmail)
      !== normalizedEmail(editingCustomer.contactEmail);

    await adminService.updateCustomer(editingCustomer.id, payload);

    if (emailChanged && editingCustomer.userId) {
      setSelectedIds((previous) => new Set(previous).add(editingCustomer.userId));
    }

    if (assignmentsChanged) {
      try {
        await adminService.assignEmployeesToCustomer(editingCustomer.id, assignedEmployees);
      } catch (error) {
        setEditingCustomer(null);
        setAlert({
          type: 'error',
          message: `Customer details were saved, but employee assignments could not be updated: ${error.message || 'Please try the assignment again.'}`
        });
        fetchCustomers();
        return;
      }
    }

    setEditingCustomer(null);
    setAlert({
      type: 'success',
      message: emailChanged
        ? 'Customer updated successfully. The customer is selected; send a new invitation for the changed login e-mail.'
        : 'Customer updated successfully.'
    });
    fetchCustomers();
  };

  const handleCopyLoginLink = (link, idx) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link).then(() => {
        setCopiedIdx(idx);
        setTimeout(() => setCopiedIdx(null), 2000);
      }).catch(() => {});
    }
  };

  // Customers that can be invited (those with a linked user account).
  const invitableCustomers = customers.filter((c) => c.userId && c.isActive);
  const allSelected = invitableCustomers.length > 0
    && invitableCustomers.every((c) => selectedIds.has(c.userId));

  const toggleSelect = (userId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (invitableCustomers.length && invitableCustomers.every((c) => prev.has(c.userId))) {
        return new Set();
      }
      return new Set(invitableCustomers.map((c) => c.userId));
    });
  };

  const handleSendInvitations = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Send the welcome invitation to ${selectedIds.size} selected active customer(s)? Existing working login links are reused. The email explains what to do if they have already received an invitation.`)) return;
    try {
      setInviting(true);
      setAlert(null);
      const report = await adminService.sendInvitations([...selectedIds]);
      setInviteReport(report);
      setSelectedIds(new Set());
      fetchCustomers();
    } catch (error) {
      setAlert({ type: 'error', message: error.message || 'Failed to send invitations.' });
    } finally {
      setInviting(false);
    }
  };

  const handleViewPlacementHistory = async (customer) => {
    try {
      const details = await adminService.getCustomerById(customer.id);
      setHistoryCustomer(details || customer);
    } catch (error) {
      // List responses may already contain the full v2 history; keep that useful
      // fallback visible if a mixed-version backend has no detail projection yet.
      setHistoryCustomer(customer);
      setAlert({ type: 'error', message: error.message || 'Could not refresh placement history.' });
    }
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Customer Management</PageTitle>
        <HeaderActions>
          <Button variant="outline" onClick={handleSendInvitations} disabled={inviting || selectedIds.size === 0}>
            <FiMail style={{ marginRight: '8px' }} />
            {inviting ? 'Sending…' : `Send Invitations${selectedIds.size ? ` (${selectedIds.size})` : ''}`}
          </Button>
          <Button onClick={() => setShowAddModal(true)}>
            <FiPlus style={{ marginRight: '8px' }} />
            Add Customer
          </Button>
        </HeaderActions>
      </PageHeader>
      
      <Card padding="md">
        {alert && (
          <AlertMessage type={alert.type}>
            {alert.message}
          </AlertMessage>
        )}
        
        <FiltersContainer>
          <FilterGroup>
            <FilterLabel htmlFor="industry">Industry</FilterLabel>
            <FilterSelect
              id="industry"
              name="industry"
              value={filters.industry}
              onChange={handleFilterChange}
            >
              <option value="">All Industries</option>
              {industries.map((industry) => (
                <option key={industry} value={industry}>
                  {industry}
                </option>
              ))}
            </FilterSelect>
          </FilterGroup>
          
          <FilterGroup>
            <FilterLabel htmlFor="isActive">Status</FilterLabel>
            <FilterSelect
              id="isActive"
              name="isActive"
              value={filters.isActive}
              onChange={handleFilterChange}
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </FilterSelect>
          </FilterGroup>
          
          <SearchContainer>
            <SearchIcon>
              <FiSearch />
            </SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search by name or email..."
              defaultValue={filters.search}
              onChange={handleSearchChange}
            />
          </SearchContainer>
        </FiltersContainer>
        
        {loading ? (
          <EmptyState>Loading customers...</EmptyState>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <tr>
                    <TableHeadCell style={{ width: '36px' }}>
                      <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} title="Select all on this page" />
                    </TableHeadCell>
                    <TableHeadCell>Company Name</TableHeadCell>
                    <TableHeadCell>CVR</TableHeadCell>
                    <TableHeadCell>Contact Email</TableHeadCell>
                    <TableHeadCell>Industry</TableHeadCell>
                    <TableHeadCell>Status</TableHeadCell>
                    <TableHeadCell>Approval emails</TableHeadCell>
                    <TableHeadCell>Created</TableHeadCell>
                    <TableHeadCell>Invoicing</TableHeadCell>
                    <TableHeadCell>Placements</TableHeadCell>
                    <TableHeadCell>Actions</TableHeadCell>
                  </tr>
                </TableHead>
                <tbody>
                  {customers.length > 0 ? (
                    customers.map(customer => (
                      <TableRow key={customer.id}>
                        <TableCell>
                          {customer.userId && customer.isActive && (
                            <input
                              type="checkbox"
                              checked={selectedIds.has(customer.userId)}
                              onChange={() => toggleSelect(customer.userId)}
                              title={`Select ${customer.name}`}
                            />
                          )}
                        </TableCell>
                        <TableCell>{customer.name}</TableCell>
                        <TableCell>{customer.cvr || '—'}</TableCell>
                        <TableCell>{customer.contactEmail}</TableCell>
                        <TableCell>{customer.industry}</TableCell>
                        <TableCell>
                          <Badge status={customer.isActive}>
                            {customer.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell title={customer.approvalReminderSchedule?.description}>
                          {customer.approvalReminderSchedule
                            ? customer.approvalReminderSchedule.enabled
                              ? 'Mondays at 06:00, when needed'
                              : customer.approvalReminderSchedule.description
                            : customer.approvalReminderFrequency === 'off' ? 'Off' : 'Mondays at 06:00; open Edit to check'}
                        </TableCell>
                        <TableCell>{new Date(customer.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell>{renderInvoicingStatus(customer.id)}</TableCell>
                        <TableCell>
                          {customer.assignedEmployees && customer.assignedEmployees.length > 0
                            ? `${customer.assignedEmployees.length} employees`
                            : 'None'}
                          <HistoryButton
                            type="button"
                            onClick={() => handleViewPlacementHistory(customer)}
                            title={`View placement history for ${customer.name}`}
                          >
                            <FiClock /> History{getPlacements(customer).length ? ` (${getPlacements(customer).length})` : ''}
                          </HistoryButton>
                        </TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingCustomer(customer)}
                            aria-label={`Edit ${customer.name}`}
                          >
                            <FiEdit style={{ marginRight: '6px' }} />
                            Edit
                          </Button>
                          <SupportMirrorButton userId={customer.userId} name={customer.name} disabled={!customer.isActive} />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={11} style={{ textAlign: 'center' }}>
                        No customers found matching your filters.
                      </TableCell>
                    </TableRow>
                  )}
                </tbody>
              </Table>
            </TableContainer>
            
            <Pagination>
              <PaginationInfo>
                Showing {Math.min((pagination.page - 1) * 10 + 1, pagination.total)} to {Math.min(pagination.page * 10, pagination.total)} of {pagination.total} customers
              </PaginationInfo>
              <PaginationButtons>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handlePrevPage}
                  disabled={pagination.page === 1}
                >
                  Previous
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleNextPage}
                  disabled={pagination.page === pagination.totalPages}
                >
                  Next
                </Button>
              </PaginationButtons>
            </Pagination>
          </>
        )}
      </Card>

      {historyCustomer && (
        <Modal
          title={`Placement history — ${historyCustomer.name}`}
          size="lg"
          onClose={() => setHistoryCustomer(null)}
        >
          <PlacementHistory
            placements={getPlacements(historyCustomer)}
            perspective="customer"
            emptyMessage="No current or historical placements are available for this customer."
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
            <Button onClick={() => setHistoryCustomer(null)}>Close</Button>
          </div>
        </Modal>
      )}
      
      {/* Invitation result */}
      {inviteReport && (
        <Modal title="Customer invitations" size="lg" onClose={() => setInviteReport(null)}>
          <p>
            Processed <strong>{inviteReport.total}</strong> invitation(s) —{' '}
            <strong>{inviteReport.queued || 0}</strong> queued for delivery
            {inviteReport.rateLimit ? ` (mail limit ${inviteReport.rateLimit.limit} per ${inviteReport.rateLimit.windowMinutes} min)` : ''}.
            {!inviteReport.mailConfigured && (
              <> SMTP is not configured, so no e-mails were sent. Configure SMTP and retry; customer access has not changed.</>
            )}
          </p>
          {inviteBatchId && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '12px', fontSize: '0.9rem' }}>
              <span>
                <strong>{inviteCounts.sent}</strong> sent · <strong>{inviteCounts.queued}</strong> waiting · <strong>{inviteCounts.failed}</strong> failed
                {inviteBatch && inviteBatch.complete ? ' — done' : ''}
                {inviteBatch && !inviteBatch.complete && inviteBatch.estimatedMinutes > 0 ? ` — about ${inviteBatch.estimatedMinutes} min to go` : ''}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => navigate(`/admin/email?batchId=${encodeURIComponent(inviteBatchId)}`)}
              >
                <FiMail />
                Open E-mail delivery
              </Button>
            </div>
          )}
          <div style={{ maxHeight: '340px', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--ve-ink-600)' }}>
                  <th style={{ padding: '4px 6px' }}>Name</th>
                  <th style={{ padding: '4px 6px' }}>E-mail</th>
                  <th style={{ padding: '4px 6px' }}>Result</th>
                </tr>
              </thead>
              <tbody>
                {(inviteReport.results || []).map((r, i) => {
                  // Live outbox status once the batch poll has it; null when
                  // the mail was never queued (placeholder / link relayed).
                  const delivery = r.skipped ? null : describeDelivery(r, inviteBatch);
                  return (
                  <tr key={i} style={{ borderTop: '1px solid var(--surface-subtle)' }}>
                    <td style={{ padding: '4px 6px' }}>{r.name || '—'}</td>
                    <td style={{ padding: '4px 6px' }}>{r.email}</td>
                    <td style={{ padding: '4px 6px' }}>
                      {r.skipped
                        ? (r.message || 'Skipped')
                        : delivery
                          ? <span style={{ color: DELIVERY_COLORS[delivery.tone] }}>{delivery.text}</span>
                          : r.loginLink
                            ? (
                              // Mail was NOT sent — the backend returns the login
                              // link so the admin can relay it manually.
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <input
                                  readOnly
                                  value={r.loginLink}
                                  onFocus={(e) => e.target.select()}
                                  style={{ flex: 1, minWidth: '200px', fontFamily: 'monospace', fontSize: '12px', padding: '4px 6px' }}
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleCopyLoginLink(r.loginLink, i)}
                                >
                                  <FiCopy />
                                  {copiedIdx === i ? 'Copied' : 'Copy'}
                                </Button>
                              </div>
                            )
                            : r.placeholder
                              ? 'Placeholder e-mail — set a real address first'
                              : 'Prepared'}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
            <Button onClick={() => setInviteReport(null)}>Close</Button>
          </div>
        </Modal>
      )}

      {/* Add Customer Modal */}
      {showAddModal && (
        <Modal
          title="Add New Customer"
          onClose={() => setShowAddModal(false)}
        >
          <CustomerForm 
            onSubmit={handleCreateCustomer}
            onCancel={() => setShowAddModal(false)}
          />
        </Modal>
      )}

      {/* Shared admin/support customer edit. contactEmail is the linked user's
          login identifier, so this is also the supported e-mail-change path. */}
      {editingCustomer && (
        <Modal
          title={`Edit Customer — ${editingCustomer.name}`}
          onClose={() => setEditingCustomer(null)}
        >
          <CustomerForm
            customer={editingCustomer}
            isEditing
            onSubmit={handleUpdateCustomer}
            onCancel={() => setEditingCustomer(null)}
          />
        </Modal>
      )}
      
      {/* Customer Created Modal — no temporary password anymore: access is a
          personal login link mailed via Send Invitations (feedback #1). */}
      {createdInfo && (
        <Modal
          title="Customer Created"
          onClose={() => setCreatedInfo(null)}
        >
          <p>The customer {createdInfo.name} was created.</p>
          <p>
            No password is needed. To give them access, select the customer in the
            list and use <strong>Send Invitations</strong> — they receive an e-mail
            with a personal login link.
            {' '}An active customer can also enter their registered email on the Tempus homepage to request access.
          </p>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
            <Button onClick={() => setCreatedInfo(null)}>
              Close
            </Button>
          </div>
        </Modal>
      )}
    </PageContainer>
  );
};

export default Customers;
